import { getSymbol, Label, Modal, Select, SubmitButton, Table } from "@xola/ui-kit";
import clsx from "clsx";
import { FormField, InputField, SecondaryButton } from "common";
import { useFormikContext } from "formik";
import { isUndefined, round } from "lodash";
import React, { useEffect, useState } from "react";
import { AddOn, Experience, LineItemCode, ProductCatalogItem, PurchaseRule, PurchaseRuleShortcutType } from "types";
import { array, number, object, ref } from "yup";
import { Currency } from "../../Common/Currency";
import { ProductImage } from "../../Common/ProductImage";
import { RadioInput } from "../../Common/RadioInput";
import { XolaFormik } from "../../Common/XolaFormik";
import {
    DiscountPricingValue,
    getCatalogByItem,
    getDefaultAction,
    getInitialDiscountType,
    getPackageDiscountAction,
    getPurchaseAction,
    getShortcutType,
    PricingSelectorFormValues,
    useLineItemTemplateByCode,
} from "./PricingSelectorHelper";
import { useSeller } from "@/hooks/useAccount";
import { getAddOnPriceRange, isChoiceAddOn } from "@/models/CatalogItem";

interface EditAddonPricingModalProps {
    catalogAddOns: ProductCatalogItem[];
    isOpen: boolean;
    purchaseRule?: PurchaseRule;
    experience: Experience;
    basePrice?: number; // TODO not needed
    onChange: (discountPricingValue: DiscountPricingValue[]) => void;
    onClose: () => void;
}

export const EditAddonPricingModal = ({
    catalogAddOns,
    isOpen,
    purchaseRule,
    experience,
    onChange,
    onClose,
    basePrice,
}: EditAddonPricingModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose} onSubmit={onsubmit}>
            <ModalContent
                purchaseRule={purchaseRule}
                catalogAddOns={catalogAddOns}
                experience={experience}
                basePrice={basePrice}
                onChange={onChange}
                onClose={onClose}
            />
        </Modal>
    );
};

type EditPricingSelectorProps = Omit<EditAddonPricingModalProps, "isOpen">;

const validationSchema = array(
    object({
        discount: number()
            .required("is required")
            .typeError("invalid number")
            .when("shortcutType", {
                is: PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT,
                then: number().max(ref("basePrice"), "Discount should be less than or equal to Base Price"),
            })
            .when("shortcutType", {
                is: PurchaseRuleShortcutType.PERCENT,
                then: number().max(100, "Discount should not be greater than 100"),
            }),
    }),
);

const ModalContent = ({
    purchaseRule,
    catalogAddOns,
    experience,
    onChange,
    onClose,
    basePrice,
}: EditPricingSelectorProps) => {
    const title = "Edit Add-On Terms";
    const { data: discountTemplate } = useLineItemTemplateByCode(LineItemCode.PACKAGE_DISCOUNT);

    const handleFormSubmit = async (values: PricingSelectorFormValues[]) => {
        const discountPricingValue: DiscountPricingValue[] = [];

        getDiscountAction(values, discountPricingValue);

        onChange(discountPricingValue);
    };

    const getDiscountAction = (
        values: PricingSelectorFormValues[],
        discountPricingValue: DiscountPricingValue[],
    ): DiscountPricingValue[] => {
        for (const value of values) {
            const catalogItem = getCatalogByItem(catalogAddOns, value.catalogItem) ?? value.catalogItem;
            if (isChoiceAddOn(catalogItem)) {
                getDiscountAction(value.items ?? [], discountPricingValue);
            }

            const purchaseAction = getPurchaseAction(purchaseRule, value.catalogItem.template?.id);
            const action = purchaseAction ?? getDefaultAction();
            const discountAction = getPackageDiscountAction(value, action, catalogItem, discountTemplate);
            discountPricingValue.push({
                action: discountAction,
            });
        }

        return discountPricingValue;
    };

    const [discountType, setDiscountType] = useState<PurchaseRuleShortcutType | undefined>(undefined);
    const initialValues = getInitialValues(catalogAddOns, basePrice, purchaseRule, discountType);

    useEffect(() => {
        setDiscountType(getInitialDiscountType(initialValues));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <XolaFormik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
        >
            {({ handleSubmit, values }) => {
                return (
                    <>
                        <Modal.Header>{title}</Modal.Header>

                        <Modal.Body>
                            <div className="flex items-center space-x-1 pb-2">
                                <ProductImage product={experience} size="small" />
                                <Label>{experience.name}</Label>
                            </div>

                            <Table>
                                <Table.Head>
                                    <Table.Row>
                                        <Table.Header>Name</Table.Header>
                                        <Table.Header>Base Price</Table.Header>
                                        <Table.Header>
                                            <DiscountHeader
                                                discountType={discountType}
                                                setDiscountType={setDiscountType}
                                            />
                                        </Table.Header>
                                        <Table.Header>Final Price</Table.Header>
                                    </Table.Row>
                                </Table.Head>

                                <Table.Body isStriped>
                                    {values.map((value, index) => {
                                        if (isChoiceAddOn(value.catalogItem)) {
                                            return (
                                                <React.Fragment key={value.catalogItem.id}>
                                                    <Table.Row className="justify-between border-t border-gray-light bg-gray-lighter pl-3">
                                                        <Table.Cell>
                                                            <Label className="whitespace-normal break-all">
                                                                {value.catalogItem.name}
                                                            </Label>
                                                        </Table.Cell>
                                                        <Table.Cell />
                                                        <Table.Cell />
                                                        <Table.Cell />
                                                    </Table.Row>
                                                    {value.items?.map((item, itemIndex) => {
                                                        return (
                                                            <TableRow
                                                                key={item.catalogItem.id}
                                                                value={item}
                                                                index={index}
                                                                discountType={discountType}
                                                                subIndex={itemIndex}
                                                            />
                                                        );
                                                    })}
                                                </React.Fragment>
                                            );
                                        }

                                        return (
                                            <TableRow
                                                key={value.catalogItem.id}
                                                value={value}
                                                index={index}
                                                discountType={discountType}
                                            />
                                        );
                                    })}
                                </Table.Body>
                            </Table>
                        </Modal.Body>

                        <Modal.Footer>
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                            <SubmitButton type="submit" onClick={handleSubmit}>
                                Save
                            </SubmitButton>
                        </Modal.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};

export const getInitialValues = (
    catalogAddOns: ProductCatalogItem[],
    basePrice?: number,
    purchaseRule?: PurchaseRule,
    discountType?: PurchaseRuleShortcutType,
) => {
    const initialValues: PricingSelectorFormValues[] = [];

    for (const catalogAddOn of catalogAddOns) {
        const catalogItem = getCatalogByItem(catalogAddOns, catalogAddOn) ?? catalogAddOn;
        if (isChoiceAddOn(catalogItem) && catalogItem.items) {
            const items = [];
            for (const item of catalogItem.items) {
                items.push(getInitialValue(item, basePrice, purchaseRule, discountType));
            }

            initialValues.push({
                discount: 0,
                basePrice: 0,
                shortcutType: PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT,
                catalogItem: catalogItem,
                items: items,
            });
        } else {
            initialValues.push(getInitialValue(catalogItem, basePrice, purchaseRule, discountType));
        }
    }

    return initialValues;
};

const getInitialValue = (
    catalogItem: ProductCatalogItem,
    basePrice?: number,
    purchaseRule?: PurchaseRule,
    discountType?: PurchaseRuleShortcutType,
): PricingSelectorFormValues => {
    const purchaseAction = getPurchaseAction(purchaseRule, catalogItem.template?.id);
    const action = purchaseAction ?? getDefaultAction();
    const price = (basePrice ? basePrice : getAddOnPriceRange(catalogItem as AddOn).price.lowest) ?? 0;
    const shortcutType = discountType ? discountType : getShortcutType(action);

    const discount = Math.abs(round(action.shortcut?.amount ?? 0, 2));
    const finalPrice =
        shortcutType === PurchaseRuleShortcutType.PERCENT
            ? Math.abs(round(price - (discount / 100) * price, 2))
            : Math.abs(discount > price ? price : round(price - discount, 2));

    return {
        catalogItem: catalogItem,
        basePrice: price,
        shortcutType: shortcutType,
        discount: discount,
        finalPrice: finalPrice,
    };
};

export const DiscountHeader = ({
    discountType,
    setDiscountType,
}: {
    discountType?: PurchaseRuleShortcutType;
    setDiscountType: (discountType?: PurchaseRuleShortcutType) => void;
}) => {
    const seller = useSeller();

    return (
        <span className="flex space-x-3">
            <span>Adjustment</span>
            <RadioInput
                checked={discountType === PurchaseRuleShortcutType.PERCENT}
                onChange={() => setDiscountType(PurchaseRuleShortcutType.PERCENT)}
            >
                %
            </RadioInput>
            <RadioInput
                checked={discountType === PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT}
                name=""
                onChange={() => setDiscountType(PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT)}
            >
                {getSymbol(seller.currency)}
            </RadioInput>
            <RadioInput checked={discountType === undefined} onChange={() => setDiscountType(undefined)}>
                Mixed
            </RadioInput>
        </span>
    );
};

const TableRow = ({
    value,
    index,
    discountType,
    subIndex,
}: {
    value: PricingSelectorFormValues;
    index: number;
    discountType: PurchaseRuleShortcutType | undefined;
    subIndex?: number;
}) => {
    const { setFieldValue, errors } = useFormikContext<PricingSelectorFormValues[]>();
    const seller = useSeller();
    const { discount } = value;
    const price = value.basePrice ?? 0;
    let finalPrice = price;
    if (value.shortcutType === PurchaseRuleShortcutType.PERCENT) {
        finalPrice = Math.abs(round(price - (discount / 100) * price, 2));
    } else if (value.shortcutType === PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT) {
        finalPrice = Math.abs(discount > price ? price : round(price - discount, 2));
    } else {
        finalPrice = discount;
    }

    const hasSubIndex = !isUndefined(subIndex);
    const discountKey = hasSubIndex ? `${index}.items.${subIndex}.discount` : `${index}.discount`;
    const shortcutTypeKey = hasSubIndex ? `${index}.items.${subIndex}.shortcutType` : `${index}.shortcutType`;

    return (
        <Table.Row key={value.catalogItem.id} className={clsx("justify-between", hasSubIndex && "bg-gray-lighter")}>
            <Table.Cell className={clsx("w-1/5", hasSubIndex && "ml-2 border-l border-gray-darker")}>
                <span className="whitespace-normal break-all">{value.catalogItem.name}</span>
            </Table.Cell>
            <Table.Cell className="w-1/5">
                <Currency>{value.basePrice}</Currency>
            </Table.Cell>
            <Table.Cell className="w-1/3">
                <FormField label="" name={discountKey}>
                    <div className="flex">
                        <InputField
                            required
                            type="number"
                            className="prevent-scroll w-full rounded-tr-none rounded-br-none border-gray-light"
                            value={value.discount}
                        />
                        <Select
                            className="w-28 rounded-none"
                            value={value.shortcutType}
                            disabled={discountType !== undefined}
                            onChange={(e: any) => setFieldValue(shortcutTypeKey, e.target.value)}
                        >
                            <option value={PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT}>
                                {getSymbol(seller.currency)} Off
                            </option>
                            <option value={PurchaseRuleShortcutType.PERCENT}>% Off</option>
                            <option value={PurchaseRuleShortcutType.ABSOLUTE_FINAL_PRICE}>Final Price</option>
                        </Select>
                    </div>
                    {errors?.[index]?.discount ? (
                        <span className="text-sm font-bold text-danger">{errors?.[index]?.discount}</span>
                    ) : null}
                </FormField>
            </Table.Cell>
            <Table.Cell className="w-1/5">
                <Currency>{finalPrice}</Currency>
            </Table.Cell>
        </Table.Row>
    );
};
